import { DIFFICULTY_LEVELS } from "../../Difficulty/Difficulty";
import { formatDate } from "../../../utils/formatDate";
import { getApiErrorMessage } from "../../../utils/apiError";
import { subtopicsOf, toNumber, trimName } from "../matrix/matrixModel";

// A subject's tasks (TZ 2.2), as GET question_folders/<uuid>/tasks/ and my_tasks/ give them:
// {uuid, kind: "write" | "review", folder: {uuid, name, stream, parent}, assignee, topic | null, subtopics, planned,
// authors, questions, due_date, comment, created_by, created, updated, cancelled, row, in_matrix, progress, done,
// overdue}. A write task counts the questions its assignee wrote since it was given: its progress is {submitted,
// approved}, each {A, B, C, none}. A review task is by authors (`authors`, `questions` null) or by chosen questions
// (`questions`: [{uuid, code, difficulty, status, vote}], vote the assignee's "UPVOTED" | "DOWNVOTED" | null); its
// progress is {total, voted, up, down}: done when the assignee has voted on each. `done` and `overdue` are the server's.

export const TASK_KINDS = ["write", "review"];
// A count of a level in a task, as in a matrix row.
export const MAX_TASK_COUNT = 999;
export const MAX_TASK_COMMENT = 2000;
export const MAX_TASK_QUESTIONS = 500;

export const personName = (user) => [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || user?.username || "";

// "Алгебра › Теңсіздіктер; Тізбектер +2", or the topic alone when the task is about it whole.
export function taskTopic(t, task) {
  if (!task.topic) return t("folders.tasks.no_topic");
  const names = subtopicsOf(task).map((s) => trimName(s.name));
  const topic = trimName(task.topic.name);
  if (!names.length) return topic;
  return `${topic} › ${names.slice(0, 2).join("; ")}${names.length > 2 ? ` +${names.length - 2}` : ""}`;
}

// A review task by chosen questions (not by authors).
export const byQuestions = (task) => Array.isArray(task?.questions);

// What a task is, as its card's title: its topic, or whose / how many questions to review.
export const taskTitle = (t, task) =>
  task.kind === "write"
    ? taskTopic(t, task)
    : byQuestions(task)
      ? t("folders.tasks.review_questions_title", { count: task.questions.length })
      : t("folders.tasks.review_title", { count: (task.authors || []).length });

// A write task's levels that have a plan: [{level, planned, approved, submitted}].
export const writeLevels = (task) =>
  DIFFICULTY_LEVELS.filter((level) => toNumber(task.planned?.[level]) > 0).map((level) => ({
    level,
    planned: toNumber(task.planned[level]),
    approved: toNumber(task.progress?.approved?.[level]),
    submitted: toNumber(task.progress?.submitted?.[level]),
  }));

// How far a task is, 0..1: approved of planned (each level up to its plan), or voted of all its questions.
export function taskShare(task) {
  if (task.kind === "review") {
    const total = toNumber(task.progress?.total);
    return total ? toNumber(task.progress?.voted) / total : 0;
  }
  const levels = writeLevels(task);
  const planned = levels.reduce((sum, l) => sum + l.planned, 0);
  return planned ? levels.reduce((sum, l) => sum + Math.min(l.approved, l.planned), 0) / planned : 0;
}

// A due date ("2026-10-30") read as that day here, not as UTC midnight.
export const dueDate = (value) => (value ? new Date(`${value}T00:00:00`) : null);
export const formatDue = (value, language) => formatDate(dueDate(value), language);
// Today as the date field wants it ("2026-10-28"), for its `min`.
export const todayInput = () => {
  const now = new Date();
  return [now.getFullYear(), String(now.getMonth() + 1).padStart(2, "0"), String(now.getDate()).padStart(2, "0")].join("-");
};

// A refused task (400 {code: "invalid_task", reason}) in words; else the server's detail or `fallbackKey`.
export function taskErrorMessage(t, err, fallbackKey) {
  const data = err?.response?.data;
  if (data?.code === "invalid_task" && data.reason) {
    return t(`folders.tasks.errors.${data.reason}`, { field: data.field || "", defaultValue: data.detail || t(fallbackKey) });
  }
  if (data?.code === "topic_in_use") return t("folders.tasks.errors.topic_in_use", { defaultValue: data.detail || t(fallbackKey) });
  return getApiErrorMessage(t, err, fallbackKey);
}

// The bank's list of a review task's questions (?task=), `left`: only those its assignee has not voted on yet.
export const reviewLink = (task, { left = false } = {}) =>
  `/questions?folder=${task.folder?.uuid}&task=${task.uuid}${left ? "&task_left=true" : ""}`;
